import { Injectable, InjectionToken, inject } from '@angular/core';

import { OptionalNonRootToken } from '@frontend/sports/host-app/sports-product/feature/non-root-token';
import { WidgetLayoutResponse } from '@frontend/sports/types/components/widget';
import { Observable, of, switchMap } from 'rxjs';

export interface IWidgetExperiment {
    changeLayout$(widgetLayout: WidgetLayoutResponse): Observable<WidgetLayoutResponse>;
}

export const WIDGET_EXPERIMENTS = new InjectionToken<IWidgetExperiment>('WIDGET_EXPERIMENTS');

/**
 * This service is for injecting any custom logic for experimenting with widgets.
 * The generic widget layout/slot cannot be changed for widget-specific experiments.
 */
@Injectable()
export class WidgetExperimentService {
    /**
     * Injected widget experiments in a random order.
     */
    private readonly widgetExperiments = inject<IWidgetExperiment[]>(OptionalNonRootToken(WIDGET_EXPERIMENTS), { optional: true });

    filterWidgetsForExperiments$(widgetLayout: WidgetLayoutResponse): Observable<WidgetLayoutResponse> {
        if (!this.widgetExperiments?.length) {
            return of(widgetLayout);
        }

        /**
         * Iterate over on the widget experiments.
         * Always taking the previous layout after modification and passing it to the next experiment.
         * The last experiment will return the final layout.
         *
         * Please note that it is not recommended to run multiple experiments parallel on the widgets but if it is needed always cross check the other modifications.
         *
         * @example
         * Default -> Experiment 1 -> Experiment 2 -> Experiment 3 -> Final layout
         *
         */
        return this.widgetExperiments.reduce(
            (acc$, experiment) => acc$.pipe(switchMap((layout) => experiment.changeLayout$(layout))),
            of(widgetLayout),
        );
    }
}
